import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { sendFeedback, type FeedbackKind } from '../api/feedbackApi';

/** Product feedback (SRS #106): rate a feature, score NPS, report a bug, suggest an idea. */
const FEATURES = ['search', 'chat', 'comparison', 'alerts', 'reports', 'dashboard', 'team', 'billing', 'other'];

export const FeedbackForm: React.FC<{ initialKind?: FeedbackKind; onDone?: () => void }> = ({ initialKind = 'feature', onDone }) => {
  const location = useLocation();
  const [kind, setKind] = useState<FeedbackKind>(initialKind);
  const [rating, setRating] = useState<number | null>(null);
  const [feature, setFeature] = useState('other');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await sendFeedback({ kind, rating: rating ?? undefined, comment: comment.trim() || undefined, page: location.pathname, feature: kind === 'feature' ? feature : undefined });
      setSent(true); onDone?.();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not send'); }
    finally { setBusy(false); }
  };

  const muted = 'var(--color-text-muted, var(--ds-text-muted))';
  const border = 'var(--color-border-subtle, var(--ds-border))';
  const chip = (active: boolean): React.CSSProperties => ({ padding: '5px 10px', borderRadius: 999, border: `1px solid ${active ? 'var(--ds-brand-blue)' : border}`, background: active ? 'var(--ds-brand-blue)' : 'transparent', color: active ? 'var(--ds-text-on-dark)' : 'inherit', cursor: 'pointer', fontSize: 12.5 });
  const scoreBtn = (n: number, active: boolean): React.CSSProperties => ({ width: 30, height: 30, borderRadius: 6, border: `1px solid ${active ? 'var(--ds-brand-blue)' : border}`, background: active ? 'var(--ds-brand-blue)' : 'transparent', color: active ? 'var(--ds-text-on-dark)' : 'inherit', cursor: 'pointer', fontSize: 12.5, fontWeight: 600 });
  const max = kind === 'nps' ? 10 : 5;
  const needRating = kind === 'feature' || kind === 'nps';
  const needText = kind === 'bug' || kind === 'idea';

  if (sent) return <div style={{ padding: '16px 0', textAlign: 'center' }}><div style={{ fontWeight: 700 }}>Thank you.</div><div style={{ fontSize: 13, color: muted }}>Every piece of feedback is read by the team.</div></div>;

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {([['feature', 'Rate a feature'], ['nps', 'Recommend?'], ['bug', 'Report a problem'], ['idea', 'Suggest an idea']] as [FeedbackKind, string][]).map(([k, l]) => (
          <button key={k} type="button" style={chip(kind === k)} onClick={() => { setKind(k); setRating(null); }}>{l}</button>
        ))}
      </div>
      {kind === 'feature' && (
        <label style={{ fontSize: 13 }}>Which feature?
          <select value={feature} onChange={(e) => setFeature(e.target.value)} style={{ display: 'block', marginTop: 4, padding: '6px 8px', borderRadius: 6, border: `1px solid ${border}`, width: '100%' }}>
            {FEATURES.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </label>
      )}
      {needRating && (
        <div>
          <div style={{ fontSize: 13, marginBottom: 6 }}>{kind === 'nps' ? 'How likely are you to recommend PolicyOra to a colleague? (0 = not at all, 10 = extremely)' : 'How would you rate it? (1 = poor, 5 = excellent)'}</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {Array.from({ length: kind === 'nps' ? 11 : 5 }, (_, i) => (kind === 'nps' ? i : i + 1)).map((n) => (
              <button key={n} type="button" aria-label={`${n} of ${max}`} style={scoreBtn(n, rating === n)} onClick={() => setRating(n)}>{n}</button>
            ))}
          </div>
        </div>
      )}
      <label style={{ fontSize: 13 }}>{needText ? (kind === 'bug' ? 'What happened, and what did you expect?' : 'What would you like PolicyOra to do?') : 'Anything you want to add? (optional)'}
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} maxLength={4000} required={needText}
                  style={{ display: 'block', marginTop: 4, width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 8, border: `1px solid ${border}`, font: 'inherit', fontSize: 13.5 }} />
      </label>
      {error && <div style={{ color: 'var(--ds-danger-text)', fontSize: 13 }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="submit" disabled={busy || (needRating && rating === null)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--ds-brand-blue)', color: 'var(--ds-text-on-dark)', fontWeight: 700, cursor: 'pointer' }}>{busy ? 'Sending…' : 'Send feedback'}</button>
      </div>
    </form>
  );
};

export default FeedbackForm;
